import { Phone, MapPin, Clock, NavigationArrow } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import useSiteSettings from "@/hooks/useSiteSettings";

export default function LocationHours() {
    const { t } = useI18n();
    const { settings } = useSiteSettings();
    const hoursList = settings.hours || [];
    return (
        <section id="contact" data-testid="location" className="bg-background py-20 md:py-28">
            <div className="mx-auto w-full max-w-7xl px-4 md:px-8 grid lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
                            {t("location.title")}
                        </p>
                        <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mt-2">
                            405 Foxon Blvd
                            <span className="block text-primary">New Haven, CT</span>
                        </h2>
                    </div>

                    <div className="space-y-4">
                        <Row icon={<MapPin size={20} weight="fill" />} title="Address">
                            405 Foxon Blvd, New Haven, CT 06513
                        </Row>
                        <Row icon={<Phone size={20} weight="fill" />} title="Phone">
                            <a href="tel:+12034666226" className="hover:text-primary" data-testid="contact-phone">
                                (203) 466-6226
                            </a>
                        </Row>
                        <Row icon={<Clock size={20} weight="fill" />} title={t("location.hours")}>
                            <ul className="space-y-1 text-sm">
                                {hoursList.map((h, i) => (
                                    <li key={i} className="flex justify-between gap-6">
                                        <span className="font-bold uppercase tracking-widest text-xs">
                                            {h.days}
                                        </span>
                                        <span className="text-muted-foreground">
                                            {h.open} – {h.close}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p
                                className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest ${
                                    settings.is_open
                                        ? "bg-emerald-100 text-emerald-900"
                                        : "bg-red-100 text-red-900"
                                }`}
                            >
                                <span
                                    className={`relative inline-flex h-2 w-2 rounded-full ${
                                        settings.is_open
                                            ? "bg-emerald-500 pulse-dot text-emerald-500"
                                            : "bg-red-500"
                                    }`}
                                />
                                {settings.is_open
                                    ? t("location.today_peak")
                                    : "Closed · ordering paused"}
                            </p>
                        </Row>
                    </div>

                    <a
                        href="https://www.google.com/maps/dir/?api=1&destination=405+Foxon+Blvd,+New+Haven,+CT+06513"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="get-directions"
                        className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-7 py-3 font-bold uppercase tracking-wider text-sm transition hover:-translate-y-0.5"
                    >
                        <NavigationArrow size={16} weight="fill" />
                        {t("location.directions")}
                    </a>
                </div>

                <div className="lg:col-span-3 rounded-3xl overflow-hidden border border-border h-[420px] lg:h-auto min-h-[420px]">
                    <iframe
                        title="J&W Deli Map"
                        data-testid="map-embed"
                        src="https://maps.google.com/maps?q=405+Foxon+Blvd,+New+Haven,+CT&t=&z=15&ie=UTF8&iwloc=&output=embed"
                        className="w-full h-full"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </div>
        </section>
    );
}

function Row({ icon, title, children }) {
    return (
        <div className="flex gap-4 items-start">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-primary/10 text-primary shrink-0">
                {icon}
            </span>
            <div>
                <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1">{title}</p>
                <div className="text-foreground">{children}</div>
            </div>
        </div>
    );
}
